import { useRouter } from "next/router";
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { isCsChatEnabled } from "../config";
import { createCsChatClientTokenGetter } from "../useCsChatAttachmentToken";
import { fetchCsChatChannelSnapshot } from "./channelLoader";
import { CsChatUnreadContext } from "./context";
import {
  csChatUnreadReducer,
  initialCsChatUnreadState,
  selectDisplayUnreadCount,
} from "./state";

/** フォアグラウンド polling 間隔。visible のときだけ発火する。 */
const POLLING_INTERVAL_MS = 60_000;

/**
 * CS チャットの未読数を `_app` 常駐で一元管理する Provider。
 *
 * 旧実装では SideMenuBar / `/support` がそれぞれ mount 時に `/client/channel` を
 * fetch していたため、persistent layout 不使用の karte ではページ遷移のたびに
 * remount = 遷移時にしか未読数が更新されず、「開いても消えない・離れると消える・
 * 開いた瞬間に増える」という挙動になっていた。ここに状態を持ち上げ、明示的な
 * 更新トリガー（mount / route 遷移 / focus / visibility / polling）と、`/support`
 * 表示中はバッジ 0・markRead 後グレース期間、で再設計する。
 *
 * WS 接続は張らない（未読のリアルタイム化は non-goal）。markRead の送信は
 * `/support` の `useCsChatConnection` に閉じており、この Provider からは送らない。
 */
export const CsChatUnreadProvider: FC<{
  children: ReactNode;
  createToken: () => Promise<string>;
  workerBaseUrl: string | undefined;
  chatPagePathname: string;
}> = ({ children, createToken, workerBaseUrl, chatPagePathname }) => {
  const router = useRouter();
  const enabled = isCsChatEnabled(workerBaseUrl);

  const [state, dispatch] = useReducer(
    csChatUnreadReducer,
    initialCsChatUnreadState,
  );

  const createTokenRef = useRef(createToken);
  createTokenRef.current = createToken;

  // JWT を TTL 内キャッシュする getter。polling / focus の再取得でも mutation は
  // 高々 15 分に 1 回（TTL）に抑えられる。添付 I/O 用と同じ client token だが、
  // キャッシュは Provider 専用に独立させる。
  const getTokenRef = useRef<ReturnType<
    typeof createCsChatClientTokenGetter
  > | null>(null);
  if (getTokenRef.current === null) {
    getTokenRef.current = createCsChatClientTokenGetter({
      createToken: () => createTokenRef.current(),
    });
  }

  const inFlightRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  const refetch = useCallback(async () => {
    const getToken = getTokenRef.current;
    if (!enabled || inFlightRef.current || !getToken) {
      return;
    }

    inFlightRef.current = true;
    dispatch({ type: "fetchStart" });

    const abortController = new AbortController();
    abortRef.current = abortController;

    try {
      const data = await fetchCsChatChannelSnapshot({
        getToken: () => getToken(),
        signal: abortController.signal,
        workerBaseUrl,
      });

      // unmount / 後続トリガーで中断された場合は state を更新しない。
      if (abortController.signal.aborted) {
        return;
      }

      dispatch({
        type: "fetchSuccess",
        channelId: data.channelId,
        unreadCount: data.unreadCount,
        at: Date.now(),
      });
    } catch {
      if (abortController.signal.aborted) {
        return;
      }

      dispatch({ type: "fetchError" });
    } finally {
      inFlightRef.current = false;
      if (abortRef.current === abortController) {
        abortRef.current = null;
      }
    }
  }, [enabled, workerBaseUrl]);

  // 初回ロード。認証済み領域に常駐する Provider のため常に最新化する。
  useEffect(() => {
    refetch();
  }, [refetch]);

  // route 遷移完了ごとに最新化（旧実装の「遷移ごとに更新」を一元化して維持）。
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleRouteChangeComplete = () => {
      refetch();
    };

    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, [enabled, refetch, router.events]);

  // 他タブ・離席からの復帰で追従。focus と visibilitychange は同時発火し得るが、
  // inFlight ガードでリクエストは多重化しない。
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleFocus = () => {
      refetch();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refetch();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, refetch]);

  // フォアグラウンド polling（visible のときのみ）。他ページ滞在中の新着検知。
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        refetch();
      }
    }, POLLING_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [enabled, refetch]);

  // unmount 時に in-flight fetch を中断する。
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const notifyMarkRead = useCallback(() => {
    dispatch({ type: "markRead", at: Date.now() });
  }, []);

  const isOnSupportPage = router.pathname === chatPagePathname;
  const displayUnreadCount = selectDisplayUnreadCount(state, isOnSupportPage);

  const value = useMemo(
    () => ({
      channelId: state.channelId,
      status: state.status,
      displayUnreadCount,
      notifyMarkRead,
      refetch,
    }),
    [
      state.channelId,
      state.status,
      displayUnreadCount,
      notifyMarkRead,
      refetch,
    ],
  );

  return (
    <CsChatUnreadContext.Provider value={value}>
      {children}
    </CsChatUnreadContext.Provider>
  );
};
